import arrowLeftIcon from "@tabler/icons/outline/arrow-left.svg";
import React from "react";
import { defineMessages, useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { HStack, Text } from "@/components/ui";
import SvgIcon from "@/components/ui/icon/svg-icon";
import { cn } from "@/utils/cn";

const sizes = {
  md: "p-4 sm:rounded-xl",
  lg: "p-4 sm:p-6 sm:rounded-xl",
  xl: "p-4 sm:p-10 sm:rounded-3xl",
};

const messages = defineMessages({
  back: { id: "card.back.label", defaultMessage: "Back" },
});

export type CardSizes = keyof typeof sizes;
export type CardVariants = "default" | "rounded" | "slim";

interface CardProps {
  /** The type of card. */
  variant?: CardVariants;
  /** Card size preset. */
  size?: CardSizes;
  /** Extra classnames for the <div> element. */
  className?: string;
  /** Elements inside the card. */
  children: React.ReactNode;
  ref?: React.Ref<HTMLDivElement>;
}

/** An opaque backdrop to hold a collection of related elements. */
const Card = ({
  children,
  variant = "default",
  size = "md",
  className,
  ref,
  ...filteredProps
}: CardProps) => (
  <div
    ref={ref}
    {...filteredProps}
    className={cn(
      {
        "bg-white text-gray-900 shadow-lg dark:bg-gray-900 dark:text-gray-100 dark:shadow-none":
          variant === "rounded",
        [sizes[size]]: variant === "rounded",
        "py-4": variant === "slim",
      },
      className,
    )}
  >
    {children}
  </div>
);

interface CardHeaderProps {
  backHref?: string;
  onBackClick?: (event: React.MouseEvent) => void;
  className?: string;
  children?: React.ReactNode;
}

/**
 * Card header container with back button.
 * Typically holds a CardTitle.
 */
const CardHeader: React.FC<CardHeaderProps> = ({
  className,
  children,
  backHref,
  onBackClick,
}): React.JSX.Element => {
  const intl = useIntl();

  const renderBackButton = () => {
    if (!backHref && !onBackClick) {
      return null;
    }

    const Comp: React.ElementType = backHref ? Link : "button";
    const backAttributes = backHref
      ? { to: backHref }
      : { onClick: onBackClick };

    return (
      <Comp
        {...backAttributes}
        className="mt-0.5 rounded-full text-gray-900 focus:ring-2 focus:ring-primary-500 dark:text-gray-100"
        aria-label={intl.formatMessage(messages.back)}
      >
        <SvgIcon src={arrowLeftIcon} className="size-6 rtl:rotate-180" />
        <span className="sr-only" data-testid="back-button">
          {intl.formatMessage(messages.back)}
        </span>
      </Comp>
    );
  };

  return (
    <HStack alignItems="start" space={2} className={className}>
      {renderBackButton()}

      {children}
    </HStack>
  );
};

interface CardTitleProps {
  title: React.ReactNode;
}

/** A card's title. */
const CardTitle: React.FC<CardTitleProps> = ({ title }): React.JSX.Element => (
  <Text
    size="xl"
    weight="bold"
    tag="h1"
    className="w-full"
    data-testid="card-title"
    truncate
  >
    {title}
  </Text>
);

interface CardBodyProps {
  /** Classnames for the <div> element. */
  className?: string;
  /** Children to appear inside the card. */
  children: React.ReactNode;
}

/** A card's body. */
const CardBody: React.FC<CardBodyProps> = ({
  className,
  children,
}): React.JSX.Element => (
  <div data-testid="card-body" className={className}>
    {children}
  </div>
);

export { Card, CardHeader, CardTitle, CardBody };
